import React, { useState, useEffect, ReactNode } from 'react';
import { UserSession } from '../../domain/models';
import { AuthContextType } from './auth-types';
import { AuthContext } from './auth-context';
import { databaseConfig } from '../../config/database';
import {
  API_ONLINE_EVENT,
  shouldPromoteOfflineSession,
} from '../../offline/offlineConnectivityEvents';

const SESSION_KEY = 'cashier_session';
const REMEMBER_KEY = 'cashier_remember_me';

interface AuthProviderProps {
  children: ReactNode;
}

function parseSession(raw: string): UserSession | null {
  try {
    const session: UserSession = JSON.parse(raw);
    if (session?.token?.expiresAt) {
      session.token.expiresAt = new Date(session.token.expiresAt);
    }
    if (session?.loginTime) {
      session.loginTime = new Date(session.loginTime);
    }
    return session;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userSession, setUserSession] = useState<UserSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerIdRef = React.useRef<number | null>(null);

  const getRemembered = () => {
    try {
      return localStorage.getItem(REMEMBER_KEY) === '1';
    } catch {
      return false;
    }
  };

  const persistSession = (session: UserSession, rememberMe: boolean) => {
    // Browser-scoped session: always localStorage so all tabs share the live session.
    const raw = JSON.stringify(session);
    localStorage.setItem(SESSION_KEY, raw);
    localStorage.setItem(REMEMBER_KEY, rememberMe ? '1' : '0');
    // Clear legacy tab-scoped copy if present.
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  };

  const clearPersistedSession = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(REMEMBER_KEY);
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
  };

  const clearRefreshTimer = () => {
    if (refreshTimerIdRef.current != null) {
      window.clearTimeout(refreshTimerIdRef.current);
      refreshTimerIdRef.current = null;
    }
  };

  const refreshSession = async (session: UserSession): Promise<UserSession | null> => {
    const baseUrl = databaseConfig.mongodb.apiBaseUrl;
    const refreshToken = session.token.refreshToken;
    if (!refreshToken) return null;

    try {
      const res = await fetch(`${baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) return null;
      const body = await res.json();
      const data = body?.data;

      if (!data?.token?.accessToken || !data?.token?.refreshToken) return null;

      const nextSession: UserSession = {
        user: data.user,
        token: {
          accessToken: data.token.accessToken,
          refreshToken: data.token.refreshToken,
          expiresAt: new Date(data.token.expiresAt),
        },
        branchId: data.branchId,
        loginTime: new Date(data.loginTime || Date.now()),
        sessionMode: 'online',
      };

      const rememberMe = getRemembered();
      persistSession(nextSession, rememberMe);
      setUserSession(nextSession);
      return nextSession;
    } catch {
      return null;
    }
  };

  // Restore browser-scoped session on app load
  useEffect(() => {
    const checkStoredSession = async () => {
      try {
        const storedRaw =
          localStorage.getItem(SESSION_KEY) ||
          // Migrate legacy tab-scoped sessions into browser scope once.
          sessionStorage.getItem(SESSION_KEY);

        if (!storedRaw) return;

        const session = parseSession(storedRaw);
        if (!session?.token) {
          clearPersistedSession();
          return;
        }

        // Promote legacy sessionStorage → localStorage so other tabs see it.
        if (!localStorage.getItem(SESSION_KEY)) {
          persistSession(session, getRemembered());
        }

        if (session.sessionMode === 'offline') {
          setUserSession(session);
          return;
        }

        if (
          session.token.expiresAt &&
          new Date(session.token.expiresAt).getTime() > Date.now()
        ) {
          setUserSession(session);
        } else if (session.token.refreshToken) {
          const refreshed = await refreshSession(session);
          if (!refreshed) {
            clearPersistedSession();
          }
        } else {
          clearPersistedSession();
        }
      } catch (error) {
        console.error('Error checking stored session:', error);
        clearPersistedSession();
      } finally {
        setIsLoading(false);
      }
    };

    void checkStoredSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep tabs in sync when another tab logs in / out / refreshes tokens.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== SESSION_KEY && event.key !== REMEMBER_KEY) return;

      if (event.key === SESSION_KEY && !event.newValue) {
        setUserSession(null);
        clearRefreshTimer();
        return;
      }

      if (event.key === SESSION_KEY && event.newValue) {
        const session = parseSession(event.newValue);
        if (!session?.token) return;
        if (session.sessionMode === 'offline') {
          setUserSession(session);
          return;
        }
        if (
          session.token.expiresAt &&
          new Date(session.token.expiresAt).getTime() > Date.now()
        ) {
          setUserSession(session);
        } else if (session.token.refreshToken) {
          void refreshSession(session);
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Schedule automatic refresh shortly before access token expiration.
  useEffect(() => {
    clearRefreshTimer();
    if (userSession?.sessionMode === 'offline') return;
    if (!userSession?.token?.expiresAt) return;

    const exp = new Date(userSession.token.expiresAt).getTime();
    const now = Date.now();
    const refreshInMs = exp - now - 30_000; // 30s early

    if (refreshInMs <= 0) {
      void refreshSession(userSession);
      return;
    }

    refreshTimerIdRef.current = window.setTimeout(() => {
      void refreshSession(userSession);
    }, refreshInMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession?.token?.accessToken, userSession?.token?.expiresAt]);

  // When connectivity returns (browser online OR FO health probe), promote offline sessions.
  useEffect(() => {
    const tryPromote = () => {
      if (
        !shouldPromoteOfflineSession({
          sessionMode: userSession?.sessionMode,
          hasRefreshToken: Boolean(userSession?.token.refreshToken),
          apiOnline: true,
        })
      ) {
        return;
      }
      void refreshSession(userSession!).then((refreshed) => {
        if (refreshed) {
          setUserSession({ ...refreshed, sessionMode: 'online' });
        }
      });
    };
    window.addEventListener('online', tryPromote);
    window.addEventListener(API_ONLINE_EVENT, tryPromote);
    return () => {
      window.removeEventListener('online', tryPromote);
      window.removeEventListener(API_ONLINE_EVENT, tryPromote);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession?.sessionMode, userSession?.token?.refreshToken]);

  const login = (session: UserSession, rememberMe: boolean) => {
    setUserSession(session);
    persistSession(session, rememberMe);
  };

  const logout = () => {
    const session = userSession;
    const remembered = getRemembered();
    if (session && !remembered) {
      window.dispatchEvent(
        new CustomEvent('incashflow:offline-auth-logout', {
          detail: {
            username: session.user.username,
            branchId: session.branchId,
          },
        }),
      );
    }
    setUserSession(null);
    clearPersistedSession();
    clearRefreshTimer();
  };

  const updateSessionUser = (patch: Partial<UserSession['user']>) => {
    setUserSession((prev) => {
      if (!prev) return prev;
      const next: UserSession = {
        ...prev,
        user: { ...prev.user, ...patch },
      };
      persistSession(next, getRemembered());
      return next;
    });
  };

  const value: AuthContextType = {
    userSession,
    isAuthenticated: !!userSession,
    isLoading,
    login,
    logout,
    updateSessionUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
